/**
 * Draft helpers shared by the four care-plan forms (Phase 4, ASM-03):
 * dirty tracking, 30 s autosave while the plan is a DRAFT, the "Saved hh:mm" indicator and the
 * unsaved-changes guard for Close/Back.
 */

import React from 'react';
import AutosaveIndicator from '../../../common/AutosaveIndicator';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { useToast } from '../../../common/Toast';
import { useAutosave } from '../../../../hooks/useAutosave';
import { useUnsavedChangesGuard } from '../../../../hooks/useUnsavedChangesGuard';
import { useDirtySnapshot, DirtySnapshotCaptureProps } from '../../../../hooks/useDirtySnapshot';
import { describeApiError } from '../../../../utils/apiErrors';

export interface UseCarePlanDraftOptions {
  /** Serialised form state */
  getSnapshot: () => string;
  /** Payload for onSave / onAutosave */
  getSaveData: () => Record<string, unknown>;
  /** Persists a draft without closing the form; omit to turn autosave off */
  onAutosave?: (data: Record<string, unknown>) => Promise<void>;
  /** Autosave runs only while true (existing DRAFT the user can edit) */
  enabled: boolean;
  /** Clears an autosave stop when the record or status changes */
  resetKey?: unknown;
}

export interface UseCarePlanDraftResult {
  captureProps: DirtySnapshotCaptureProps;
  indicator: React.ReactNode;
  dialog: React.ReactNode;
  /** Runs `proceed` now, or after confirming when there are unsaved changes */
  confirmLeave: (proceed: () => void) => void;
}

export function useCarePlanDraft({
  getSnapshot,
  getSaveData,
  onAutosave,
  enabled,
  resetKey,
}: UseCarePlanDraftOptions): UseCarePlanDraftResult {
  const { showToast } = useToast();
  const dirty = useDirtySnapshot(getSnapshot);
  const autosaveEnabled = enabled && !!onAutosave;

  const autosave = useAutosave({
    enabled: autosaveEnabled,
    isDirty: dirty.isDirty,
    resetKey,
    save: async () => {
      if (!onAutosave) return;
      const snapshot = dirty.takeSnapshot();
      await onAutosave(getSaveData());
      dirty.markClean(snapshot);
    },
    onError: (err, stopped) => {
      showToast(
        stopped
          ? `Autosave paused: ${describeApiError(err)}`
          : describeApiError(err, 'Autosave failed. Your changes are not saved yet.'),
        { variant: stopped ? 'warning' : 'error' }
      );
    },
  });

  const guard = useUnsavedChangesGuard(dirty.isDirty);

  const indicator = autosaveEnabled ? (
    <AutosaveIndicator
      saving={autosave.saving}
      lastSavedAt={autosave.lastSavedAt}
      stopped={autosave.stopped}
      hasError={!!autosave.error}
    />
  ) : null;

  return {
    captureProps: dirty.captureProps,
    indicator,
    dialog: <ConfirmDialog {...guard.dialogProps} />,
    confirmLeave: guard.confirmNavigation,
  };
}

export default useCarePlanDraft;
